import { useCallback, useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import LogCompareCharts from "./LogCompareCharts";
import { ACCENT } from "./accent";
import { getComparison } from "./api/newTelemetry";
import { APP_BASE } from "./paths";

const fmtDuration = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m ? `${m}m ${sec}s` : `${sec}s`;
};

const peakVibe = (summary) => {
  const v = summary?.vibration || {};
  const nums = [v.vibex_max, v.vibey_max, v.vibez_max].filter((x) => typeof x === "number");
  return nums.length ? Math.max(...nums) : null;
};

const show = (v, suffix = "") => (v == null ? "—" : `${v}${suffix}`);

// (label, extractor) — one row per metric, one column per log.
const METRICS = [
  ["Duration", (l) => fmtDuration(l.duration_s)],
  ["Vehicle", (l) => l.vehicle_type || "—"],
  ["Firmware", (l) => l.firmware_version || "—"],
  ["Min voltage", (l) => show(l.summary?.battery?.volt_min, " V")],
  ["Consumed", (l) => show(l.summary?.battery?.consumed_mah, " mAh")],
  ["Peak vibration", (l) => show(peakVibe(l.summary))],
  ["Max roll error", (l) => show(l.summary?.attitude?.roll_err_max_deg, "°")],
  ["Min satellites", (l) => show(l.summary?.gps?.sats_min)],
  ["Max HDOP", (l) => show(l.summary?.gps?.hdop_max)],
  ["GPS fix", (l) => (l.summary?.gps?.fix_min == null ? "—" : l.summary.gps.fix_min < 3 ? "Lost 3D" : "OK")],
  ["Error events", (l) => (l.summary?.errors?.length ?? 0)],
];

const ComparisonPage = () => {
  const theme = useTheme();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getComparison(id);
      setData(res);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const cellSx = { borderColor: alpha(theme.palette.divider, 0.5), color: theme.palette.text.primary };
  const logs = data ? [{ ...data.base, _base: true }, ...data.compares] : [];

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Button
        component={RouterLink}
        to={APP_BASE}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ color: theme.palette.text.secondary, textTransform: "none", mb: 2 }}
      >
        Log Analyzer
      </Button>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="secondary" size={28} />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : !data ? null : (
        <>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {data.comparison.name}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, mb: 3 }}>
            {data.base.file_name} vs {data.compares.length} log{data.compares.length === 1 ? "" : "s"}
          </Typography>

          {/* Auto highlights */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(ACCENT, 0.4)}`,
              backgroundColor: alpha(ACCENT, 0.06),
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <LightbulbOutlinedIcon sx={{ color: ACCENT, fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                Highlights
              </Typography>
            </Box>
            {data.highlights.map((h, i) => (
              <Typography key={i} variant="body2" sx={{ color: theme.palette.text.secondary, py: 0.25 }}>
                • {h}
              </Typography>
            ))}
          </Box>

          {/* Side-by-side stat table */}
          <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
            STATS
          </Typography>
          <Box sx={{ overflowX: "auto", mb: 3 }}>
            <Table size="small" sx={{ minWidth: 480 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...cellSx, color: theme.palette.text.secondary }}>Metric</TableCell>
                  {logs.map((l) => (
                    <TableCell key={l.id} sx={cellSx}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>
                          {l.file_name}
                        </Typography>
                        {l._base && <Chip label="base" size="small" sx={{ height: 16, fontSize: 9, backgroundColor: alpha(ACCENT, 0.2), color: ACCENT }} />}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {METRICS.map(([label, extract]) => (
                  <TableRow key={label}>
                    <TableCell sx={{ ...cellSx, color: theme.palette.text.secondary }}>{label}</TableCell>
                    {logs.map((l) => (
                      <TableCell key={l.id} sx={cellSx}>
                        {extract(l)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Overlaid charts */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
            OVERLAID CHARTS
          </Typography>
          <LogCompareCharts logs={logs} />
        </>
      )}
    </Box>
  );
};

export default ComparisonPage;
